import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { StatCard, HealthBadge, SectionTitle, Empty, Spinner } from "@/components/common";
import { FolderKanban, CalendarClock, FileText, ListChecks, Plus, ArrowRight, Boxes } from "lucide-react";

export default function Home() {
  const navigate = useNavigate();
  const { data: projects, isLoading } = useQuery({
    queryKey: ["projects"],
    queryFn: async () => (await api.get("/projects")).data,
  });

  const totals = (projects || []).reduce(
    (acc, p) => ({
      meetings: acc.meetings + (p.meeting_count || 0),
      docs: acc.docs + (p.doc_count || 0),
      actions: acc.actions + (p.open_actions || 0),
    }),
    { meetings: 0, docs: 0, actions: 0 }
  );

  return (
    <div className="space-y-8 animate-fade-up">
      <div className="rounded-2xl brand-gradient text-white p-8 relative overflow-hidden">
        <div className="absolute -right-16 -top-16 w-72 h-72 rounded-full bg-[#29abe2]/20 blur-3xl" />
        <div className="relative z-10">
          <div className="text-[11px] font-semibold tracking-[0.2em] text-[#c9a227] uppercase">Workspace Home</div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-2">Business360</h1>
          <p className="text-white/70 mt-2 max-w-xl text-sm">
            Every engagement, from the first requirement call to a client-ready document pack and a tracked SAP delivery plan.
          </p>
          <Button onClick={() => navigate("/projects/new")} data-testid="new-project-hero"
            className="mt-6 bg-white text-[#0f2557] hover:bg-white/90 gap-2 font-semibold">
            <Plus size={16} /> New Project
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={FolderKanban} label="Projects" value={projects?.length || 0} accent="navy" testid="stat-projects" />
        <StatCard icon={CalendarClock} label="Meetings" value={totals.meetings} accent="cyan" testid="stat-meetings" />
        <StatCard icon={FileText} label="Documents" value={totals.docs} accent="cyan" testid="stat-docs" />
        <StatCard icon={ListChecks} label="Open Actions" value={totals.actions} accent="gold" testid="stat-actions" />
      </div>

      <div>
        <SectionTitle eyebrow="Engagements" title="Your Projects"
          action={<Button variant="outline" onClick={() => navigate("/projects/new")} className="gap-2" data-testid="new-project-btn"><Plus size={15} /> New</Button>} />
        {isLoading ? <Spinner /> : (projects || []).length === 0 ? (
          <Empty title="No projects yet"
            hint="Create your first engagement — define the SAP landscape, connected systems and your delivery team to get started."
            action={<Button onClick={() => navigate("/projects/new")} className="gap-2"><Plus size={16} /> Create project</Button>} />
        ) : (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
            {projects.map((p) => (
              <button key={p.id} onClick={() => navigate(`/projects/${p.id}`)} data-testid={`project-card-${p.id}`}
                className="text-left rounded-xl border bg-card p-5 card-hover">
                <div className="flex items-start justify-between">
                  <div className="grid place-items-center w-11 h-11 rounded-lg bg-[#0f2557]/10 dark:bg-[#29abe2]/10 text-[#0f2557] dark:text-[#29abe2]">
                    <Boxes size={20} />
                  </div>
                  <HealthBadge status={p.health} />
                </div>
                <div className="mt-4 font-semibold text-foreground">{p.name}</div>
                <div className="text-sm text-muted-foreground">{p.client_name} · {p.industry}</div>
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {(p.systems?.sap_modules || []).slice(0, 4).map((m) => (
                    <span key={m} className="text-[10px] font-medium bg-secondary px-2 py-0.5 rounded">{m}</span>
                  ))}
                  {(p.systems?.sap_modules || []).length > 4 && <span className="text-[10px] text-muted-foreground">+{p.systems.sap_modules.length - 4}</span>}
                </div>
                <div className="flex items-center justify-between mt-4 pt-4 border-t text-xs text-muted-foreground">
                  <span>{p.meeting_count || 0} meetings · {p.doc_count || 0} docs</span>
                  <ArrowRight size={14} className="text-[#29abe2]" />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
